import { faDownload } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Paper, styled, useTheme } from "@mui/material";
import { gridClasses, GridColDef } from "@mui/x-data-grid";
import { GetFieldLookupItem, useFormMetadata, useLookups } from "@ngt/forms";
import { DateTime } from "luxon";
import { FC, useContext, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Attachment, FormDefinitions, Permissions } from "../api/dtos";
import PermissionsContext from "../contexts/auth/PermissionsContext";
import LotContext from "../contexts/data/LotContext";
import ProjectContext from "../contexts/data/ProjectContext";
import StageContext from "../contexts/data/StageContext";
import LotAttachmentsContext from "../contexts/LotAttachmentsContext";
import GridActions from "./GridActions";
import { StyledIconButton } from "./StyledButton";
import CollapsibleTablePremium from "./tables/CollapsibleTablePremium";
import { createCustomToolbarProps } from "./utility/CustomToolbarProps";

const StyledPaper = styled(Paper) (({theme}) => ({
    marginBottom: theme.spacing(2)
}));

const useAttachmentColumns = () => {
    const {data: permissions} = useContext(PermissionsContext);

    const metadata = useFormMetadata(FormDefinitions.Attachment);

    const {data: lookups, loading: _lookupsLoading} = useLookups(metadata);

    const params = useParams();

    const lotId = params?.lotId;
    const stageId = params?.stageId;
    const projectId = params?.projectId;

    const attachmentColumns = useMemo(() => {
        const columns: GridColDef<Attachment>[] = [
            {
                field: "file",
                headerName: "",
                width: 50,
                renderCell: (params) => (
                    params.row.file &&
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                height: "100%"
                            }}
                        >
                            <StyledIconButton
                                //@ts-expect-error
                                component="a"
                                href={`/forms/file/download/${params.row.file?.id}`}
                                size="small"
                            >
                                <FontAwesomeIcon icon={faDownload} />
                            </StyledIconButton>
                        </Box>
                )
            },
            {
                field: "name",
                headerName: "Name",
                flex: 1
            },
            {
                field: "fileName",
                headerName: "File Name",
                flex: 1,
                valueGetter: (_value, attachment) => attachment.file?.fileName
            },
            {
                field: "modifiedDate",
                headerName: "Date Modified",
                type: "date",
                valueFormatter: (_value, attachment) => {
                    return DateTime.fromISO(attachment.modifiedDate?.toString() ?? "").toFormat("dd/MM/yyyy");
                },
                flex: 1
            },
            {
                field: "attachmentTypeId",
                headerName: "Attachment Type",
                valueGetter: (_value, attachment) => GetFieldLookupItem(lookups, 'AttachmentTypeId', attachment.attachmentTypeId)?.value,
                flex: 1
            },
        ]

        if(permissions?.includes(Permissions.LotCanDeleteAttachment) || permissions?.includes(Permissions.LotCanDeleteAttachment)) {
            columns.push({
                field: "actions",
                headerName: '',
                width: 100,
                renderCell: (params) => {
                    return (
                        <GridActions 
                            editPermission={Permissions.LotCanEditAttachment} 
                            deletePermission={Permissions.LotCanDeleteAttachment}
                            editUrl={`/projects/${projectId}/stages/${stageId}/lots/${lotId}/attachments/edit/${params.row.id}`}
                            deleteUrl={`/projects/${projectId}/stages/${stageId}/lots/${lotId}/attachments/delete/${params.row.id}`}
                        />
                    )
                },
                filterable: false,
                sortable: false,
                disableExport: true
            })
        }

        return columns;
    }, [lookups, permissions, projectId, stageId, lotId])

    return attachmentColumns;
}

interface ILotAttachmentsProps {
    lotId: number;
}

const LotAttachments:FC<ILotAttachmentsProps> = () => {
    const {data: project} = useContext(ProjectContext);
    const {data: stage} = useContext(StageContext);
    const {data: lot} = useContext(LotContext);

    const {data: attachments, loading: attachmentsLoading} = useContext(LotAttachmentsContext);

    const attachmentColumns = useAttachmentColumns();

    const customToolbarProps = useMemo(() => createCustomToolbarProps({
        exportTitle: `${project?.name} - ${stage?.name} - Lot ${lot?.number} - Attachments`,
        fileName: `${project?.name} - ${stage?.name} - Lot ${lot?.number} - Attachments`,
        includeDateTime: true
    }), [project, stage, lot]);

    const theme = useTheme();
    return (
        <StyledPaper>
            <Box
                sx={{
                    marginTop: theme.spacing(2),
                    [`& .${gridClasses.root}`]: {
                        [`& .${gridClasses.columnHeader}`]: {
                            height: 'auto !important',
                            minHeight: 56,
                        
                            [`& .${gridClasses.columnHeaderTitleContainer}`]: {
                                whiteSpace: 'normal',
                        
                                [`& .${gridClasses.columnHeaderTitle}`]: {
                                    whiteSpace: 'normal',
                                    paddingTop: theme.spacing(1.5),
                                    paddingBottom: theme.spacing(1.5) 
                                }
                            },
                        
                            [`& .${gridClasses.iconButtonContainer}`]: {
                                width: 'unset',
                            },

                            '&.column-group-header': {
                                backgroundColor: theme.palette.grey[300],
                                boxShadow: "1",
                                textAlign: 'center'
                            }
                        }
                    },
                }}
            >
                <CollapsibleTablePremium
                    density="compact"
                    startCollapsed={false} 
                    title={"Attachments"} 
                    loading={attachmentsLoading} 
                    columns={attachmentColumns}
                    rows={attachments}
                    disableColumnMenu={false}
                    disableColumnFilter={false}
                    {...customToolbarProps}
                />
            </Box>
        </StyledPaper>
    );
}

export default LotAttachments;